/**
 * Account backup / restore.
 *
 * The sealed keystore is a transparent encrypt-on-write wrapper around
 * the underlying (raw) IndexedDB KeyStore. The raw store therefore
 * holds the salt plus AES-GCM ciphertexts; dumping it verbatim is a
 * complete, passphrase-protected backup — we never touch the user's
 * passphrase during export or import.
 *
 * Export format (JSON):
 * {
 *   "version": 1,
 *   "createdAt": <ms>,
 *   "entries": [ { "key": "chat:...", "value": "<base64url>" }, ... ]
 * }
 *
 * On restore, seed a fresh device's raw keystore with every entry from
 * the bundle, then go through the normal unlock flow. The same
 * passphrase produces the same AES key via the same persisted salt,
 * so every ciphertext decrypts cleanly on the new device.
 */
import { fromBase64Url, toBase64Url } from '@chat/protocol';
import type { KeyStore } from '@chat/crypto';

export const BACKUP_VERSION = 1;

export interface BackupEntry {
  readonly key: string;
  readonly value: string;
}

export interface BackupBundle {
  readonly version: number;
  readonly createdAt: number;
  readonly entries: readonly BackupEntry[];
}

export async function exportBackup(store: KeyStore): Promise<BackupBundle> {
  const keys = await store.list('');
  const entries: BackupEntry[] = [];
  for (const key of keys) {
    const val = await store.get(key);
    if (!val) continue;
    entries.push({ key, value: toBase64Url(val) });
  }
  return { version: BACKUP_VERSION, createdAt: Date.now(), entries };
}

/**
 * V5-LOW-02 / V5-LOW-03 caps. Bundles produced from a real account
 * are well under both — the larger limit guards against a hand-
 * crafted bundle bloating IDB during restore. Per-entry value cap
 * is 1 MiB encoded (about 768 KiB plaintext post-base64url decode);
 * sealed records are typically a few KiB.
 */
const MAX_BUNDLE_ENTRIES = 10_000;
const MAX_ENTRY_VALUE_LENGTH = 1_048_576;
/**
 * V5-LOW-02: only entries under known prefixes are ever needed for
 * restore. Drop anything else so a hand-crafted bundle can't plant
 * data under arbitrary keys (e.g. a future schema's trust marker).
 */
const ALLOWED_KEY_PREFIXES: readonly string[] = ['chat:'];

function hasAllowedPrefix(key: string): boolean {
  for (const prefix of ALLOWED_KEY_PREFIXES) {
    if (key.startsWith(prefix)) return true;
  }
  return false;
}

export function parseBackupBundle(raw: unknown): BackupBundle {
  if (typeof raw !== 'object' || raw === null) throw new Error('invalid backup bundle');
  const obj = raw as {
    version?: unknown;
    createdAt?: unknown;
    entries?: unknown;
  };
  if (obj.version !== BACKUP_VERSION) {
    throw new Error(`unsupported backup version ${String(obj.version)}`);
  }
  if (!Array.isArray(obj.entries)) throw new Error('backup entries missing');
  if (obj.entries.length > MAX_BUNDLE_ENTRIES) {
    throw new Error(
      `backup contains too many entries (${String(obj.entries.length)}, max ${String(MAX_BUNDLE_ENTRIES)})`,
    );
  }
  const entries: BackupEntry[] = [];
  for (const e of obj.entries) {
    if (typeof e !== 'object' || e === null) continue;
    const cast = e as { key?: unknown; value?: unknown };
    if (
      typeof cast.key === 'string' &&
      hasAllowedPrefix(cast.key) &&
      typeof cast.value === 'string' &&
      cast.value.length <= MAX_ENTRY_VALUE_LENGTH
    ) {
      entries.push({ key: cast.key, value: cast.value });
    }
  }
  if (entries.length === 0) throw new Error('backup contains no entries');
  const createdAt = typeof obj.createdAt === 'number' ? obj.createdAt : Date.now();
  return { version: BACKUP_VERSION, createdAt, entries };
}

/**
 * Write every entry from the bundle into `store`. Existing entries
 * with the same key are overwritten. Caller is responsible for
 * guarding against writing into a store that already has a
 * different identity (check for an existing salt first).
 */
export async function importBackup(store: KeyStore, bundle: BackupBundle): Promise<void> {
  for (const entry of bundle.entries) {
    let bytes: Uint8Array;
    try {
      bytes = fromBase64Url(entry.value);
    } catch {
      throw new Error(`backup entry "${entry.key}" is corrupt`);
    }
    await store.put(entry.key, bytes);
  }
}
